import React, { useEffect, useMemo, useRef } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import htmlparser from "react-html-parser";
import { graphql, compose } from "lib/apolloClient";

import {
  RadioButton,
  Table,
  Tooltip,
  EmptyState,
  Avatar,
} from "@toddle-edu/ds-web";
import { AddClassIllustration } from "@toddle-edu/ds-theme";

import Loading from "UIComponents/Loading";
import withLoader from "UIComponents/GqlWithLoader";
import PaginationLoader from "UIComponents/PaginationLoader";
import MoreItemWithToolTipV2 from "UIComponents/MoreItemWithToolTipV2";

import { getLearningCoursesQuery } from "../../modules/Queries";

import { useI18n } from "Hooks";
import { getHTMLLabel } from "NodeEditor/Utils";
import {
  getYearDurationFormat,
  useIntersectionObserver,
  getMemoizedFunction,
} from "Utils";
import {
  COURSE_TYPE,
  getAvatarData,
} from "Teacher/routes/LearningCourses/modules/Utils";
import { getCourseSelectionHeaders } from "../../modules/Utils";

import { colors } from "Constants";

import classes from "./LearningCourseSelectionFeed.scss";

const getFeedResources = getMemoizedFunction(feed => {
  return _.map(_.get(feed, "edges", []), "node");
});

const LearningCourseSelectionFeed = ({
  courses,
  isData,
  isLoading,
  totalCount,
  courseSearchText,
  setTotalCount,
  isCurriculumPYP,
  setSelectedLearningCourse,
  selectedLearningCourse,
  isFetchingMoreData,
  loadMoreFeed,
  hasNextPage,
  setisCoursesLoading,
}) => {
  const { t } = useI18n();

  const loaderRef = useRef(null);
  const isLoaderVisible = useIntersectionObserver(loaderRef, {
    threshold: 0.7,
  });

  const isDataLoading = isLoading && !isData;

  useEffect(() => {
    setTotalCount(totalCount);
  }, [totalCount]);

  useEffect(() => {
    setisCoursesLoading(isDataLoading);
  }, [isDataLoading]);

  useEffect(() => {
    if (isLoaderVisible && hasNextPage && !isFetchingMoreData) {
      loadMoreFeed();
    }
  }, [isLoaderVisible, hasNextPage, isFetchingMoreData, loadMoreFeed]);

  const headers = getCourseSelectionHeaders({ isCurriculumPYP });

  const bodyData = useMemo(() => {
    return _.map(courses, course => {
      const { id, title, type, subjects, grades, academicYears } = course;

      const selectedItem = selectedLearningCourse?.id === course?.id;

      return {
        id: id,
        rowData: _.map(headers, ({ key }) => {
          switch (key) {
            case "RADIO_BUTTON": {
              return {
                key: "RADIO_BUTTON",
                value: (
                  <RadioButton
                    checked={selectedItem}
                    name="class-selection-feed-radio-btn"
                    size="medium"
                  />
                ),
              };
            }

            case "COURSE_NAME": {
              const { color, name } = getAvatarData({ id, title });
              const highlightedTitle = htmlparser(
                getHTMLLabel({
                  label: title,
                  searchText: courseSearchText,
                  style: { color: colors.blue400 },
                })
              );
              return {
                key,
                value: (
                  <div className={classes.courseNameContainer}>
                    <Avatar
                      color={color}
                      name={name}
                      shade="light"
                      size="small"
                      shape="square"
                      className={classes.avatar}
                    />
                    <Tooltip tooltip={title} placement="top">
                      <div className="truncate text-body">
                        {highlightedTitle}
                      </div>
                    </Tooltip>
                  </div>
                ),
              };
            }

            case "COURSE_TYPE": {
              return {
                key,
                value:
                  type == COURSE_TYPE.BLUEPRINT
                    ? t(`course:blueprint_course`)
                    : t(`course:teacher_course`) || "-",
              };
            }

            case "GRADES": {
              return {
                key,
                value: (
                  <div className="truncate">
                    <MoreItemWithToolTipV2
                      items={_.map(grades, "name")}
                      length={1}
                      noItemText="-"
                    />
                  </div>
                ),
              };
            }

            case "SUBJECTS": {
              return {
                key,
                value: (
                  <div className="truncate">
                    <MoreItemWithToolTipV2
                      items={_.map(subjects, "name")}
                      length={1}
                      noItemText="-"
                    />
                  </div>
                ),
              };
            }

            case "ACADEMIC_YEAR": {
              const academicYearsList = _.map(
                academicYears,
                ({ startDate, endDate }) =>
                  getYearDurationFormat({ startDate, endDate })
              );

              return {
                key,
                value: (
                  <div className="truncate">
                    <MoreItemWithToolTipV2
                      items={academicYearsList}
                      length={1}
                      noItemText="-"
                    />
                  </div>
                ),
              };
            }

            default: {
              return {
                key,
                value: "-",
              };
            }
          }
        }),
      };
    });
  }, [courses, selectedLearningCourse, courseSearchText]);

  const onItemClick = id => {
    const item = _.find(courses, course => course.id === id);
    const prevSelectedCourseId = _.get(selectedLearningCourse, "id", null);
    if (prevSelectedCourseId !== id) {
      setSelectedLearningCourse(item);
    }
  };

  return isDataLoading ? (
    <div className={classes.loaderContainer}>
      <Loading />
    </div>
  ) : totalCount > 0 ? (
    <div className={classes.feedContainer}>
      <Table
        headers={headers}
        data={bodyData}
        testId={`courseFlow-importFromAnotherCourseModal`}
        onRowClick={onItemClick}
      />
      {hasNextPage ? (
        <div ref={loaderRef}>
          <PaginationLoader />
        </div>
      ) : null}
    </div>
  ) : (
    <EmptyState
      illustration={AddClassIllustration}
      title={t("course:no_courses_to_import_from")}
    />
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    courseSearchText,
    academicYear,
    courseType,
    grade,
    subject,
    currentCurriculumProgramId,
  } = ownProps;

  const curriculumInterfaceType = _.get(
    state,
    "platform.currentCurriculumProgram.nodeInterfaceType",
    ""
  );

  const feedQueryVariables = {
    curriculumProgramId: currentCurriculumProgramId,
    curriculumInterfaceType,
    filters: {
      searchText: courseSearchText,
      grades: grade,
      subjects: subject,
      academicYears: academicYear,
      types: courseType,
    },
    portalType: "PLANNER",
  };

  return {
    feedQueryVariables,
    isData: true,
    isLoading: false,
  };
};

export default compose(
  connect(mapStateToProps, null),
  graphql(getLearningCoursesQuery, {
    name: "getLearningCourses",
    options: ({ feedQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: feedQueryVariables,
    }),
    props({ getLearningCourses, ownProps: { isData, isLoading } }) {
      const { node, variables, networkStatus } = getLearningCourses;

      const data = _.get(node, "learningCourse", {});
      const courses = getFeedResources(data);
      const pageInfo = _.get(data, "pageInfo", {});
      const hasNextPage = _.get(pageInfo, "hasNextPage", false);
      const endCursor = _.get(pageInfo, "endCursor", null);
      const totalCount = _.size(courses);

      return {
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        courses,
        totalCount,
        networkStatus,
        hasNextPage,
        isFetchingMoreData: networkStatus === 3,
        loadMoreFeed: () => {
          getLearningCourses.fetchMore({
            variables: {
              ...variables,
              after: endCursor,
            },
          });
        },
      };
    },
  }),
  withLoader
)(LearningCourseSelectionFeed);
